import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { FEATURE_DEFINITIONS, PLAN_ORDER } from '../../../config/planFeatures';

let nextId = 100;

const createCatalogFeatures = (planKey = '') => FEATURE_DEFINITIONS.map((definition, index) => ({
  id: index + 1,
  text: definition.label,
  key: definition.key,
  value: typeof definition.plans[planKey] === 'string' ? definition.plans[planKey] : '',
  info: definition.description,
  included: definition.plans[planKey] !== false,
  infoOpen: false,
}));

const createInitialFormData = (plan = {}, allowCustomPlanKey = false) => ({
  tierName: plan?.tierName || '',
  planKey: plan?.planKey || (allowCustomPlanKey ? '__custom__' : ''),
  customPlanKey: plan?.planKey && !PLAN_ORDER.includes(plan.planKey) ? plan.planKey : '',
  price: plan?.price ?? '',
  annualMonthlyPrice: plan?.annualMonthlyPrice ?? (plan?.annualPrice ? Number(plan.annualPrice) / 12 : ''),
  annualPrice: plan?.annualPrice ?? '',
  billingPeriod: plan?.billingPeriod || 'monthly',
  description: plan?.description || '',
  isPopular: plan?.isPopular || false,
  isActive: plan?.isActive !== undefined ? plan?.isActive : true,
  badgeText: plan?.badgeText || '',
  ctaText: plan?.ctaText || 'Get Started',
  maxForms: plan?.maxForms ?? '',
  maxSubmissions: plan?.maxSubmissions ?? '',
  maxTeamMembers: plan?.maxTeamMembers ?? '',
  maxStorageMb: plan?.maxStorageMb ?? '',
  stripePriceIdMonthly: plan?.stripePriceIdMonthly || '',
  stripePriceIdAnnual: plan?.stripePriceIdAnnual || '',
  stripeProductId: plan?.stripeProductId || '',
});

const PlanBuilderModal = ({ plan, onClose, onSave, allowCustomPlanKey = false, existingPlanKeys = [] }) => {
  const [formData, setFormData] = useState(() => createInitialFormData(plan, allowCustomPlanKey));
  const [features, setFeatures] = useState(() => createCatalogFeatures());
  const [activeSection, setActiveSection] = useState('basic');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    setFormData(createInitialFormData(plan, allowCustomPlanKey));
    setValidationError('');

    const rawFeatures = Array.isArray(plan?.features)
      ? plan.features
      : Object.entries(plan?.features && typeof plan.features === 'object' ? plan.features : {}).map(([key, value]) => ({
          key,
          text: key.replace(/_/g, ' '),
          value: typeof value === 'boolean' ? '' : String(value ?? ''),
          included: typeof value === 'boolean' ? value : true,
        }));
    const configuredEntitlements = plan?.featureEntitlements && typeof plan.featureEntitlements === 'object'
      ? plan.featureEntitlements
      : {};
    const rawByKey = new Map(rawFeatures
      .map((feature) => [feature?.key || feature?.featureKey, feature])
      .filter(([key]) => Boolean(key)));
    const catalogFeatures = createCatalogFeatures(plan?.planKey).map((feature) => {
      const raw = rawByKey.get(feature.key);
      const configured = configuredEntitlements[feature.key];
      return {
        ...feature,
        id: raw?.id ?? feature.id,
        text: raw?.text || raw?.name || feature.text,
        value: configured?.value ?? raw?.value ?? feature.value,
        info: raw?.info || raw?.infoPopup || raw?.tooltip || feature.info,
        included: typeof configured?.included === 'boolean'
          ? configured.included
          : raw?.included !== undefined ? raw.included !== false : feature.included,
      };
    });
    const catalogKeys = new Set(catalogFeatures.map((feature) => feature.key));
    const extras = [
      ...rawFeatures.filter((feature) => {
        const key = feature?.key || feature?.featureKey;
        return key && !catalogKeys.has(key);
      }),
      ...Object.entries(configuredEntitlements)
        .filter(([key]) => !catalogKeys.has(key) && !rawByKey.has(key))
        .map(([key, value]) => ({ key, text: key.replace(/_/g, ' '), ...value })),
    ].map((feature, index) => ({
      id: feature?.id ?? catalogFeatures.length + index + 1,
      text: feature?.text || feature?.name || feature?.key,
      key: feature?.key || feature?.featureKey,
      value: feature?.value ?? '',
      info: feature?.info || feature?.infoPopup || feature?.tooltip || '',
      included: feature?.included !== false,
      infoOpen: false,
    }));
    setFeatures([...catalogFeatures, ...extras]);
  }, [plan, allowCustomPlanKey]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePlanKeyChange = (planKey) => {
    handleChange('planKey', planKey);
    if (plan?.planKey) return;

    setFeatures((currentFeatures) => {
      const customFeatures = currentFeatures.filter((feature) => (
        !FEATURE_DEFINITIONS.some((definition) => definition.key === feature.key)
      ));
      return [...createCatalogFeatures(planKey), ...customFeatures];
    });
  };

  const addFeature = () => {
    nextId += 1;
    setFeatures(prev => [...prev, { id: nextId, text: '', key: '', value: '', info: '', included: true, infoOpen: false }]);
  };

  const removeFeature = (id) => {
    setFeatures(prev => prev?.filter(f => f?.id !== id));
  };

  const updateFeature = (id, field, value) => {
    setFeatures(prev => prev?.map(f => f?.id === id ? { ...f, [field]: value } : f));
  };

  const toggleInfoPanel = (id) => {
    setFeatures(prev => prev?.map(f => f?.id === id ? { ...f, infoOpen: !f?.infoOpen } : f));
  };

  const moveFeature = (index, direction) => {
    const newFeatures = [...features];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFeatures?.length) return;
    [newFeatures[index], newFeatures[targetIndex]] = [newFeatures?.[targetIndex], newFeatures?.[index]];
    setFeatures(newFeatures);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const selectedPlanKey = String(formData?.planKey || '').trim().toLowerCase();
    const planKey = selectedPlanKey === '__custom__'
      ? String(formData?.customPlanKey || '').trim().toLowerCase()
      : selectedPlanKey;
    const isCustomPlanKey = allowCustomPlanKey && !PLAN_ORDER.includes(planKey);
    const customPlanKeyIsValid = /^[a-z][a-z0-9_-]*$/.test(planKey);
    if ((!PLAN_ORDER.includes(planKey) && !isCustomPlanKey) || (isCustomPlanKey && !customPlanKeyIsValid)) {
      setValidationError(allowCustomPlanKey
        ? 'Choose a stable plan key or enter a custom key using lowercase letters, numbers, hyphens, or underscores.'
        : 'Choose one of the four stable subscriber plan keys before saving.');
      setActiveSection('basic');
      return;
    }
    if (!plan && existingPlanKeys.includes(planKey)) {
      setValidationError('That plan key is already in use. Choose a unique key before saving.');
      setActiveSection('basic');
      return;
    }
    const cleanFeatures = features?.map(({ id, text, key, value, info, included }) => ({
      id, text, key: key?.trim() || undefined, value, infoPopup: info, included,
    }));
    if (cleanFeatures.some((feature) => feature.text?.trim() && !feature.key)) {
      setValidationError('Each named feature needs a runtime key before it can be saved.');
      setActiveSection('features');
      return;
    }
    const featureByKey = new Map(cleanFeatures.filter((feature) => feature.key).map((feature) => [feature.key, feature]));
    const completeFeatures = [
      ...createCatalogFeatures(planKey).map((catalogFeature) => ({
        ...catalogFeature,
        ...featureByKey.get(catalogFeature.key),
        infoOpen: false,
      })),
      ...cleanFeatures.filter((feature) => feature.key && !FEATURE_DEFINITIONS.some((definition) => definition.key === feature.key)),
    ];
    const featureEntitlements = completeFeatures.reduce((entitlements, feature) => {
      if (feature.key) {
        entitlements[feature.key] = { included: feature.included, ...(feature.value ? { value: feature.value } : {}) };
      }
      return entitlements;
    }, {});
    const toLimit = (value) => value === '' || Number(value) === 0 ? -1 : parseInt(value, 10);
    // customPlanKey is only the input used when a new custom plan is selected.
    // The database stores the resolved value in planKey, so never persist the
    // temporary UI field as a separate column.
    const { annualMonthlyPrice, annualPrice, customPlanKey: _customPlanKey, ...rest } = formData;
    const planData = {
      ...rest,
      planKey,
      features: completeFeatures,
      featureEntitlements,
      price: rest?.price !== '' ? parseFloat(rest?.price) : 0,
      annualMonthlyPrice: annualMonthlyPrice !== '' ? parseFloat(annualMonthlyPrice) : 0,
      annualPrice: annualPrice !== '' ? parseFloat(annualPrice) : 0,
      maxForms: toLimit(rest?.maxForms),
      maxSubmissions: toLimit(rest?.maxSubmissions),
      maxTeamMembers: toLimit(rest?.maxTeamMembers),
      maxStorageMb: toLimit(rest?.maxStorageMb),
    };
    onSave(planData);
  };

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: 'FileText' },
    { id: 'pricing', label: 'Pricing', icon: 'DollarSign' },
    { id: 'limits', label: 'Limits', icon: 'Sliders' },
    { id: 'features', label: 'Features', icon: 'List' },
    { id: 'stripe', label: 'Stripe IDs', icon: 'CreditCard' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between rounded-t-lg">
          <h2 className="text-xl font-heading font-bold text-foreground">
            {plan ? 'Edit Pricing Plan' : 'Create New Pricing Plan'}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Section Tabs */}
        <div className="border-b border-border px-6 bg-muted/30">
          <div className="flex gap-1 overflow-x-auto">
            {sections?.map(s => (
              <button
                key={s?.id}
                type="button"
                onClick={() => setActiveSection(s?.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm border-b-2 transition-colors whitespace-nowrap ${
                  activeSection === s?.id
                    ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={s?.icon} size={14} />
                {s?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {validationError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <Icon name="CircleAlert" size={16} className="mt-0.5 shrink-0" />
                <span>{validationError}</span>
              </div>
            )}

            {/* ── BASIC INFO ── */}
            {activeSection === 'basic' && (
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                  <Icon name="FileText" size={16} className="text-primary" />
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Plan Name *"
                    value={formData?.tierName}
                    onChange={(e) => handleChange('tierName', e?.target?.value)}
                    placeholder="e.g., Professional"
                    required
                  />
                  {allowCustomPlanKey && (!plan || (plan?.planKey && !PLAN_ORDER.includes(plan.planKey))) ? (
                    <>
                      <Select
                        label="Plan key"
                        value={PLAN_ORDER.includes(formData?.planKey) || formData?.planKey === '__custom__' ? formData?.planKey : '__custom__'}
                        onChange={handlePlanKeyChange}
                        disabled={Boolean(plan?.planKey && PLAN_ORDER.includes(plan.planKey))}
                        options={[
                          { value: '', label: 'Choose a plan key' },
                          { value: 'free', label: 'free — Free' },
                          { value: 'starter', label: 'starter — Starter Pro' },
                          { value: 'professional', label: 'professional — Business' },
                          { value: 'enterprise', label: 'enterprise — Agency (White Label)' },
                          { value: '__custom__', label: 'Custom plan key' },
                        ]}
                      />
                      {(!plan || !PLAN_ORDER.includes(plan?.planKey)) && formData?.planKey === '__custom__' && (
                        <Input
                          label="Custom plan key *"
                          value={formData?.customPlanKey || ''}
                          onChange={(e) => handleChange('customPlanKey', e?.target?.value)}
                          placeholder="e.g., nonprofit"
                          pattern="[a-z][a-z0-9_-]*"
                          required
                        />
                      )}
                    </>
                  ) : (
                    <Select
                      label="Stable subscriber plan key"
                      value={formData?.planKey}
                      onChange={handlePlanKeyChange}
                      disabled={Boolean(plan?.planKey)}
                      options={[
                        { value: '', label: 'Choose a plan key' },
                        { value: 'free', label: 'free — Free' },
                        { value: 'starter', label: 'starter — Starter Pro' },
                        { value: 'professional', label: 'professional — Business' },
                        { value: 'enterprise', label: 'enterprise — Agency (White Label)' },
                      ]}
                    />
                  )}
                  <Input
                    label="Badge Text"
                    value={formData?.badgeText}
                    onChange={(e) => handleChange('badgeText', e?.target?.value)}
                    placeholder="e.g., Most Popular"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                  <textarea
                    value={formData?.description}
                    onChange={(e) => handleChange('description', e?.target?.value)}
                    placeholder="Brief description of this plan"
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                  />
                </div>

                <Input
                  label="CTA Button Text"
                  value={formData?.ctaText}
                  onChange={(e) => handleChange('ctaText', e?.target?.value)}
                  placeholder="e.g., Get Started"
                />

                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData?.isPopular}
                      onChange={(e) => handleChange('isPopular', e?.target?.checked)}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Mark as popular plan</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData?.isActive}
                      onChange={(e) => handleChange('isActive', e?.target?.checked)}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">Plan is active (visible to users)</span>
                  </label>
                </div>

                <p className="text-xs text-muted-foreground">
                  The plan key is used by existing subscribers and checkout links. Configure feature access from the Features tab using a stable runtime key.
                </p>
              </div>
            )}

            {/* ── PRICING ── */}
            {activeSection === 'pricing' && (
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                  <Icon name="DollarSign" size={16} className="text-primary" />
                  Pricing
                </h3>

                <Select
                  label="Billing Period"
                  value={formData?.billingPeriod}
                  onChange={(e) => handleChange('billingPeriod', e?.target?.value)}
                  options={[
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'annual', label: 'Annual' },
                    { value: 'lifetime', label: 'Lifetime' },
                    { value: 'free', label: 'Free' },
                  ]}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Monthly Price ($)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData?.price}
                        onChange={(e) => handleChange('price', e?.target?.value)}
                        placeholder="0"
                        className="w-full pl-7 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Annual Billing Equivalent ($ / month)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData?.annualMonthlyPrice}
                        onChange={(e) => {
                          const value = e?.target?.value;
                          setFormData((current) => ({
                            ...current,
                            annualMonthlyPrice: value,
                            annualPrice: value === '' ? '' : Number(value) * 12,
                          }));
                        }}
                        placeholder="0"
                        className="w-full pl-7 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Annual Contract Total ($ / year)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData?.annualPrice}
                        onChange={(e) => {
                          const value = e?.target?.value;
                          setFormData((current) => ({
                            ...current,
                            annualPrice: value,
                            annualMonthlyPrice: value === '' ? '' : Number(value) / 12,
                          }));
                        }}
                        placeholder="0"
                        className="w-full pl-7 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                      />
                    </div>
                  </div>
                </div>

                {formData?.annualPrice !== '' && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                    <Icon name="TrendingDown" size={14} className="inline mr-1" />
                    Annual contract total: ${parseFloat(formData?.annualPrice || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    {formData?.price && parseFloat(formData?.price) > 0 && ` — saves ${Math.round((1 - (parseFloat(formData?.annualMonthlyPrice) / parseFloat(formData?.price))) * 100)}% vs monthly`}
                  </div>
                )}
              </div>
            )}

            {/* ── LIMITS ── */}
            {activeSection === 'limits' && (
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Sliders" size={16} className="text-primary" />
                  Plan Limits
                </h3>
                <p className="text-sm text-muted-foreground">Set to 0 for unlimited.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Max Forms</label>
                    <input
                      type="number"
                      min="0"
                      value={formData?.maxForms}
                      onChange={(e) => handleChange('maxForms', e?.target?.value)}
                      placeholder="0 = unlimited"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">0 = unlimited forms</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Max Submissions / Month</label>
                    <input
                      type="number"
                      min="0"
                      value={formData?.maxSubmissions}
                      onChange={(e) => handleChange('maxSubmissions', e?.target?.value)}
                      placeholder="0 = unlimited"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">0 = unlimited submissions</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Max Team Seats</label>
                    <input
                      type="number"
                      min="0"
                      value={formData?.maxTeamMembers}
                      onChange={(e) => handleChange('maxTeamMembers', e?.target?.value)}
                      placeholder="0 = unlimited"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">0 = unlimited seats</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Storage Limit (MB)</label>
                    <input
                      type="number"
                      min="0"
                      value={formData?.maxStorageMb}
                      onChange={(e) => handleChange('maxStorageMb', e?.target?.value)}
                      placeholder="0 = unlimited"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Examples: 100 MB, 5,120 MB (5 GB)</p>
                  </div>
                </div>

                <div className="p-4 bg-muted/40 rounded-lg border border-border space-y-2">
                  <p className="text-sm font-medium text-foreground">Current Limits Summary</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Forms:</span>
                      <span className="font-medium text-foreground">
                        {!formData?.maxForms || parseInt(formData?.maxForms) === 0 ? 'Unlimited' : formData?.maxForms}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Submissions:</span>
                      <span className="font-medium text-foreground">
                        {!formData?.maxSubmissions || parseInt(formData?.maxSubmissions) === 0 ? 'Unlimited' : `${formData?.maxSubmissions}/mo`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Team:</span>
                      <span className="font-medium text-foreground">
                        {!formData?.maxTeamMembers || parseInt(formData?.maxTeamMembers, 10) === 0 ? 'Unlimited' : `${formData?.maxTeamMembers} seats`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Storage:</span>
                      <span className="font-medium text-foreground">
                        {!formData?.maxStorageMb || parseInt(formData?.maxStorageMb, 10) === 0
                          ? 'Unlimited'
                          : `${formData?.maxStorageMb >= 1024 ? formData?.maxStorageMb / 1024 : formData?.maxStorageMb} ${formData?.maxStorageMb >= 1024 ? 'GB' : 'MB'}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── FEATURES ── */}
            {activeSection === 'features' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                    <Icon name="List" size={16} className="text-primary" />
                    Plan Features
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFeature}
                    className="flex items-center gap-1 text-sm"
                  >
                    <Icon name="Plus" size={15} />
                    Add Feature
                  </Button>
                </div>

                {features?.length === 0 && (
                  <p className="text-sm text-muted-foreground italic py-4 text-center">
                    No features added yet. Click "Add Feature" to begin.
                  </p>
                )}

                <div className="space-y-2">
                  {features?.map((feature, index) => (
                    <div key={feature?.id} className="border border-border rounded-lg overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-2 bg-background">
                        {/* Reorder */}
                        <div className="flex flex-col gap-0.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => moveFeature(index, 'up')}
                            disabled={index === 0}
                            className={`p-0.5 rounded ${index === 0 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-primary'}`}
                          >
                            <Icon name="ChevronUp" size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveFeature(index, 'down')}
                            disabled={index === features?.length - 1}
                            className={`p-0.5 rounded ${index === features?.length - 1 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-primary'}`}
                          >
                            <Icon name="ChevronDown" size={13} />
                          </button>
                        </div>

                        <span className="text-xs text-muted-foreground w-5 shrink-0 text-center">{index + 1}.</span>
                        <label className="flex items-center gap-1 text-xs text-muted-foreground shrink-0" title="Controls access for this plan">
                          <input
                            type="checkbox"
                            checked={feature?.included !== false}
                            onChange={(e) => updateFeature(feature?.id, 'included', e?.target?.checked)}
                            className="w-3.5 h-3.5 text-primary border-border rounded focus:ring-primary"
                          />
                          Included
                        </label>

                        {/* Feature label */}
                        <input
                          type="text"
                          value={feature?.text}
                          onChange={(e) => updateFeature(feature?.id, 'text', e?.target?.value)}
                          placeholder="Feature name (e.g. Form Limit)"
                          className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <input
                          type="text"
                          value={feature?.key}
                          onChange={(e) => updateFeature(feature?.id, 'key', e?.target?.value)}
                          placeholder="runtime key"
                          className="w-32 shrink-0 px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        />

                        {/* Feature value */}
                        <input
                          type="text"
                          value={feature?.value}
                          onChange={(e) => updateFeature(feature?.id, 'value', e?.target?.value)}
                          placeholder="Value (e.g. Unlimited)"
                          className="w-32 shrink-0 px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        />

                        {/* Info toggle */}
                        <button
                          type="button"
                          onClick={() => toggleInfoPanel(feature?.id)}
                          title="Set info popup text"
                          className={`p-1.5 rounded transition-colors shrink-0 ${
                            feature?.info
                              ? 'text-primary bg-primary/10 hover:bg-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                        >
                          <Icon name="Info" size={16} />
                        </button>

                        {/* Remove */}
                        <button
                          type="button"
                          onClick={() => removeFeature(feature?.id)}
                          title="Remove feature"
                          className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>

                      {feature?.infoOpen && (
                        <div className="px-3 py-2 bg-muted/30 border-t border-border">
                          <label className="block text-xs font-medium text-muted-foreground mb-1">
                            Info Popup Text <span className="font-normal text-muted-foreground/60">(shown as tooltip to users)</span>
                          </label>
                          <textarea
                            value={feature?.info}
                            onChange={(e) => updateFeature(feature?.id, 'info', e?.target?.value)}
                            placeholder="e.g. You can create up to 10 active forms."
                            rows={2}
                            className="w-full px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {features?.length > 0 && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Info" size={12} />
                    A runtime key (for example, <code>white_label</code>) saves a structured entitlement. The label and tooltip are customer-facing and can be updated without code changes.
                  </p>
                )}
              </div>
            )}

            {/* ── STRIPE IDs ── */}
            {activeSection === 'stripe' && (
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                  <Icon name="CreditCard" size={16} className="text-primary" />
                  Stripe Integration
                </h3>
                <p className="text-sm text-muted-foreground">
                  Link this plan to Stripe products and prices for billing. Leave blank if not using Stripe for this plan.
                </p>

                <Input
                  label="Stripe Product ID"
                  value={formData?.stripeProductId}
                  onChange={(e) => handleChange('stripeProductId', e?.target?.value)}
                  placeholder="prod_..."
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Stripe Monthly Price ID"
                    value={formData?.stripePriceIdMonthly}
                    onChange={(e) => handleChange('stripePriceIdMonthly', e?.target?.value)}
                    placeholder="price_..."
                  />
                  <Input
                    label="Stripe Annual Price ID"
                    value={formData?.stripePriceIdAnnual}
                    onChange={(e) => handleChange('stripePriceIdAnnual', e?.target?.value)}
                    placeholder="price_..."
                  />
                </div>

                <div className="p-3 bg-muted/40 rounded-lg border border-border text-xs text-muted-foreground flex items-start gap-2">
                  <Icon name="Info" size={14} className="shrink-0 mt-0.5 text-primary" />
                  <span>These IDs are used when creating Stripe checkout sessions. Find them in your Stripe Dashboard under Products.</span>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="border-t border-border px-6 py-4 flex items-center justify-between bg-card rounded-b-lg">
            <div className="flex gap-2">
              {sections?.map((s, i) => (
                <button
                  key={s?.id}
                  type="button"
                  onClick={() => setActiveSection(s?.id)}
                  className={`w-2 h-2 rounded-full transition-colors ${activeSection === s?.id ? 'bg-primary' : 'bg-border hover:bg-muted-foreground'}`}
                  title={s?.label}
                />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                {plan ? 'Update Plan' : 'Create Plan'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanBuilderModal;
